// components/SalesChart.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import reportsAPI from '../api/reportsAPI';

const SalesChart = () => {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartType, setChartType] = useState('bar');
    const [timeFrame, setTimeFrame] = useState('day');
    const [compareWith, setCompareWith] = useState('none');

    useEffect(() => {
        fetchChartData();
    }, [timeFrame, compareWith]);

    const fetchChartData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await reportsAPI.getSalesTrend({
                period: timeFrame,
                compare: compareWith
            });
            
            //console.log('API Response:', response);
            
            if (response.data && response.data.success) {
                setChartData(response.data.data);
            } else {
                throw new Error(response.data?.message || 'Failed to load chart data');
            }
        } catch (err) {
            console.error('Chart data fetch error:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load sales chart data');
            setChartData(null);
        } finally {
            setLoading(false);
        }
    };

    // Transform data for Recharts
    const transformedData = useMemo(() => {
        if (!chartData || !chartData.labels || !chartData.currentPeriod) {
            return [];
        }

        const { labels, currentPeriod, previousPeriod } = chartData;
        const isComparison = compareWith !== 'none' && previousPeriod;

        return labels.map((label, index) => {
            const dataPoint = {
                name: label,
                current: currentPeriod[index] || 0
            };

            if (isComparison && previousPeriod[index] !== undefined) {
                dataPoint.previous = previousPeriod[index] || 0;
            }

            return dataPoint;
        });
    }, [chartData, compareWith]);

    // Calculate proper Y-axis domain and ticks
    const yAxisProps = useMemo(() => {
        if (!chartData?.currentPeriod) {
            return {
                domain: [0, 1000],
                ticks: [0, 250, 500, 750, 1000]
            };
        }

        const allValues = [
            ...chartData.currentPeriod,
            ...(chartData.previousPeriod || [])
        ].filter(val => val > 0);

        if (allValues.length === 0) {
            return {
                domain: [0, 1000],
                ticks: [0, 250, 500, 750, 1000]
            };
        }

        const maxValue = Math.max(...allValues);
        
        // Calculate nice intervals for Y-axis
        let interval;
        if (maxValue <= 1000) interval = 250;
        else if (maxValue <= 5000) interval = 1000;
        else if (maxValue <= 10000) interval = 2000;
        else if (maxValue <= 50000) interval = 10000;
        else interval = 25000;

        const upperBound = Math.ceil(maxValue / interval) * interval;
        const ticks = [];
        
        for (let i = 0; i <= upperBound; i += interval) {
            ticks.push(i);
        }

        return {
            domain: [0, upperBound],
            ticks: ticks
        };
    }, [chartData]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-PH', {
            style: 'currency',
            currency: 'PHP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                    <p className="font-semibold text-gray-800">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} className="text-sm" style={{ color: entry.color }}>
                            <span className="font-medium">{entry.name}:</span> {formatCurrency(entry.value)}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const CustomYAxisTick = ({ x, y, payload }) => {
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={4} textAnchor="end" fill="#666" fontSize={12}>
                    {formatCurrency(payload.value)}
                </text>
            </g>
        );
    };

    const CustomXAxisTick = ({ x, y, payload }) => {
        // For hourly view, only show every 3rd hour to avoid crowding
        const shouldShow = timeFrame === 'day' 
            ? ['00:00', '03:00', '06:00', '09:00', '12:00', '15:00', '18:00', '21:00'].includes(payload.value)
            : true;

        if (!shouldShow) return null;

        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={16} textAnchor="middle" fill="#666" fontSize={11}>
                    {payload.value}
                </text>
            </g>
        );
    };

    const renderChart = () => {
        const isComparison = compareWith !== 'none' && chartData?.previousPeriod;

        if (chartType === 'bar') {
            return (
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                        data={transformedData} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                            dataKey="name" 
                            tick={<CustomXAxisTick />}
                            interval={0}
                        />
                        <YAxis 
                            domain={yAxisProps.domain}
                            ticks={yAxisProps.ticks}
                            tick={<CustomYAxisTick />}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar 
                            dataKey="current" 
                            name={timeFrame === 'day' ? 'Today' : timeFrame === 'week' ? 'This Week' : 'This Month'}
                            fill="#3B82F6" 
                            radius={[2, 2, 0, 0]}
                        />
                        {isComparison && (
                            <Bar 
                                dataKey="previous" 
                                name={timeFrame === 'day' ? 'Yesterday' : timeFrame === 'week' ? 'Last Week' : 'Last Month'}
                                fill="#9CA3AF" 
                                radius={[2, 2, 0, 0]}
                            />
                        )}
                    </BarChart>
                </ResponsiveContainer>
            );
        } else {
            return (
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart 
                        data={transformedData} 
                        margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                            dataKey="name" 
                            tick={<CustomXAxisTick />}
                            interval={0}
                        />
                        <YAxis 
                            domain={yAxisProps.domain}
                            ticks={yAxisProps.ticks}
                            tick={<CustomYAxisTick />}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line 
                            type="monotone" 
                            dataKey="current" 
                            name={timeFrame === 'day' ? 'Today' : timeFrame === 'week' ? 'This Week' : 'This Month'}
                            stroke="#3B82F6" 
                            strokeWidth={3}
                            dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, fill: '#3B82F6' }}
                        />
                        {isComparison && (
                            <Line 
                                type="monotone" 
                                dataKey="previous" 
                                name={timeFrame === 'day' ? 'Yesterday' : timeFrame === 'week' ? 'Last Week' : 'Last Month'}
                                stroke="#9CA3AF" 
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={{ fill: '#9CA3AF', strokeWidth: 2, r: 3 }}
                                activeDot={{ r: 5, fill: '#9CA3AF' }}
                            />
                        )}
                    </LineChart>
                </ResponsiveContainer>
            );
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-96 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
                <span className="ml-3 text-gray-600">Loading chart data...</span>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Chart Header with Controls */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
                <div>
                    <h3 className="text-xl font-semibold text-gray-900">Sales Trend</h3>
                    <p className="text-gray-600 text-sm">Track your sales performance over time</p>
                </div>
                
                <div className="flex flex-wrap gap-4 mt-4 lg:mt-0">
                    {/* Chart Type Toggle */}
                    <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-gray-700">Chart:</label>
                        <select
                            value={chartType}
                            onChange={(e) => setChartType(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            disabled={loading}
                        >
                            <option value="bar">Bar</option>
                            <option value="line">Line</option>
                        </select>
                    </div>

                    {/* Time Frame Selector */}
                    <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-gray-700">View:</label>
                        <select
                            value={timeFrame}
                            onChange={(e) => setTimeFrame(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            disabled={loading}
                        >
                            <option value="day">Per Hour (Today)</option>
                            <option value="week">Per Day (Week)</option>
                            <option value="month">Per Day (Month)</option>
                        </select>
                    </div>

                    {/* Comparison Toggle */}
                    <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-gray-700">Compare:</label>
                        <select
                            value={compareWith}
                            onChange={(e) => setCompareWith(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            disabled={loading}
                        >
                            <option value="none">No Comparison</option>
                            <option value="previous_period">Previous Period</option>
                        </select>
                    </div>

                    {/* Refresh Button */}
                    <button
                        onClick={fetchChartData}
                        className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading}
                    >
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        {error}
                    </div>
                </div>
            )}

            {/* Chart Container */}
            <div className="h-80">
                {chartData ? (
                    renderChart()
                ) : error ? (
                    <div className="h-full flex flex-col items-center justify-center text-gray-500">
                        <div className="text-4xl mb-2">❌</div>
                        <p className="text-lg font-medium mb-2">Failed to load chart data</p>
                        <p className="text-sm text-gray-600 mb-4">{error}</p>
                        <button 
                            onClick={fetchChartData}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-teal-600"
                        >
                            Try Again
                        </button>
                    </div>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-500">
                        <div className="text-4xl mb-2">📊</div>
                        <p className="text-lg font-medium mb-2">No chart data available</p>
                        <p className="text-sm text-gray-600 mb-4">Click refresh to load data</p>
                        <button 
                            onClick={fetchChartData}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-teal-600"
                        >
                            Load Data
                        </button>
                    </div>
                )}
            </div>

            {/* Chart Statistics */}
            {chartData && chartData.summary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
                    <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">
                            {formatCurrency(chartData.summary.currentTotal || 0)}
                        </p>
                        <p className="text-sm text-gray-600">Current Period</p>
                    </div>
                    <div className="text-center">
                        <p className="text-2xl font-bold text-gray-600">
                            {formatCurrency(chartData.summary.previousTotal || 0)}
                        </p>
                        <p className="text-sm text-gray-600">Previous Period</p>
                    </div>
                    <div className="text-center">
                        <p className={`text-2xl font-bold ${
                            (chartData.summary.percentageChange || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                            {(chartData.summary.percentageChange || 0) > 0 ? '+' : ''}
                            {(chartData.summary.percentageChange || 0).toFixed(1)}%
                        </p>
                        <p className="text-sm text-gray-600">Change</p>
                    </div>
                    <div className="text-center">
                        <p className="text-2xl font-bold text-purple-600">
                            {formatCurrency(Math.round(chartData.summary.averageOrderValue || 0))}
                        </p>
                        <p className="text-sm text-gray-600">Average</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesChart;